import { FormHelper } from '@presentation/components/FormHelper';
import { ToastService } from '@presentation/components/Toast';
import { UserCreateSchema } from '@shared/validation/UserSchema';
import { AppContext } from '@application/context/AppContext';
import type { Role } from '@shared/types/User';
import { createTenantUser, listTenantUsers } from '@application/auth/UserDirectoryService';
import {
  findSalarieByEmail,
  linkUserToMatchingSalarie,
  normalizeEmail,
} from '@application/auth/UserSalarieLinkService';

const ROLE_OPTIONS: Array<{ value: Role; label: string; hint: string }> = [
  {
    value: 'admin',
    label: 'Administrateur',
    hint: 'Accès complet aux écrans autorisés par la licence',
  },
  {
    value: 'rh',
    label: 'Gestionnaire RH',
    hint: 'Personnel, paie, congés et modules RH sous licence',
  },
  {
    value: 'comptable',
    label: 'Comptable',
    hint: 'Comptabilité, fiscalité et modules financiers sous licence',
  },
  {
    value: 'lecture',
    label: 'Lecture seule',
    hint: 'Consultation des écrans métier autorisés par la licence, sans modification',
  },
  {
    value: 'salarie',
    label: 'Salarié',
    hint: 'Espace personnel : bulletins, congés, documents',
  },
];

export class UserFormView {
  static render(container: HTMLElement, onSaved: () => void, onCancel = onSaved): void {
    container.innerHTML = `
      <h2 style="margin-top:0;">Nouvel utilisateur</h2>
      <form id="user-form" style="max-width:640px;background:white;padding:24px;border:1px solid #e1e4e8;border-radius:8px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          ${FormHelper.field('prenom', 'Prénom', 'text', { required: true })}
          ${FormHelper.field('nom', 'Nom', 'text', { required: true })}
          ${FormHelper.field('email', 'Email', 'email', { required: true, placeholder: 'utilisateur@entreprise.ga' })}
          ${FormHelper.field('password', 'Mot de passe', 'password', { required: true, placeholder: '8 caractères minimum', autocomplete: 'new-password' })}
          ${FormHelper.select(
            'role',
            'Profil',
            ROLE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
            { required: true, value: 'rh' },
          )}
        </div>
        <p id="role-hint" style="margin:12px 0 0;font-size:13px;color:#586069;"></p>
        <p id="salarie-match" style="margin:6px 0 0;font-size:13px;color:#0a4d7e;"></p>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
          <button type="button" id="cancel" style="padding:8px 16px;background:white;color:#586069;border:1px solid #e1e4e8;border-radius:4px;cursor:pointer;">Annuler</button>
          <button type="submit" style="padding:8px 16px;background:#0a4d7e;color:white;border:none;border-radius:4px;font-weight:600;cursor:pointer;">Créer le compte</button>
        </div>
      </form>
    `;

    const form = container.querySelector<HTMLFormElement>('#user-form')!;
    const roleSelect = form.querySelector<HTMLSelectElement>('[name="role"]');
    const emailInput = form.querySelector<HTMLInputElement>('[name="email"]');
    const hintEl = container.querySelector('#role-hint');
    const matchEl = container.querySelector('#salarie-match');

    const refreshHint = (): void => {
      const role = roleSelect?.value as Role | undefined;
      const meta = ROLE_OPTIONS.find((o) => o.value === role);
      if (hintEl) hintEl.textContent = meta?.hint ?? '';
    };
    roleSelect?.addEventListener('change', refreshHint);
    refreshHint();

    // Aperçu du rattachement : l'admin voit à quelle fiche l'e-mail correspond.
    const refreshMatch = async (): Promise<void> => {
      if (!matchEl) return;
      if (roleSelect?.value !== 'salarie') {
        matchEl.textContent = '';
        return;
      }
      const ctx = AppContext.getInstance();
      const salaries = await ctx.salaries.list();
      const salarie = findSalarieByEmail(salaries, emailInput?.value);
      matchEl.textContent = salarie
        ? `Sera rattaché à la fiche ${salarie.prenom} ${salarie.nom} (matricule ${salarie.matricule}).`
        : 'Aucune fiche salarié avec cet e-mail : le compte sera rattaché automatiquement dès sa création.';
    };
    emailInput?.addEventListener('blur', () => void refreshMatch());
    roleSelect?.addEventListener('change', () => void refreshMatch());

    container.querySelector('#cancel')?.addEventListener('click', () => onCancel());

    FormHelper.bindSubmit(form, UserCreateSchema, async (data) => {
      const ctx = AppContext.getInstance();
      const tenantId = ctx.tenantContext.tenantId();
      const existing = await listTenantUsers(ctx, tenantId);
      if (existing.some((u) => normalizeEmail(u.email) === normalizeEmail(data.email))) {
        ToastService.warning('Un compte existe déjà avec cet email');
        return;
      }
      const user = await createTenantUser(ctx, tenantId, {
        email: normalizeEmail(data.email),
        password: data.password,
        nom: data.nom,
        prenom: data.prenom,
        role: data.role,
      });
      const salarie = await linkUserToMatchingSalarie(ctx, user);
      ToastService.success(
        salarie
          ? `Utilisateur ${data.prenom} ${data.nom} créé et rattaché à la fiche ${salarie.matricule}`
          : `Utilisateur ${data.prenom} ${data.nom} créé`,
      );
      onSaved();
    });
  }
}
